import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  login: (token: string) => void;
  logout: () => void;
  checkAuthStatus: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Check if authentication is disabled
  const isAuthDisabled = import.meta.env.VITE_DISABLE_AUTH === 'true';

  // Check if user is authenticated on mount
  useEffect(() => {
    // If auth is disabled, automatically set as authenticated
    if (isAuthDisabled) {
      setIsAuthenticated(true);
      setAccessToken('disabled-auth-token');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (token === 'disabled-auth-token') {
      setIsAuthenticated(false);
      setAccessToken("");
      return;
    }
    const tokenExpiry = localStorage.getItem('token_expiry');
    
    if (token && tokenExpiry) {
      const expiryTime = parseInt(tokenExpiry, 10);
      const currentTime = Date.now();
      
      if (currentTime < expiryTime) {
        setAccessToken(token);
        setIsAuthenticated(true);
      } else {
        // Token expired, clear storage
        localStorage.removeItem('access_token');
        localStorage.removeItem('token_expiry');
      }
    }
  }, [isAuthDisabled]);

  const login = (token: string) => {
    // Set token expiry to 1 hour from now (default for OAuth tokens)
    const expiryTime = Date.now() + (60 * 60 * 1000);
    
    localStorage.setItem('access_token', token);
    localStorage.setItem('token_expiry', expiryTime.toString());
    
    setAccessToken(token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_expiry');
    
    setAccessToken(null);
    setIsAuthenticated(false);
  };

  const checkAuthStatus = (): boolean => {
    // If auth is disabled, always return true
    if (isAuthDisabled) {
      return true;
    }

    const token = localStorage.getItem('access_token');
    const tokenExpiry = localStorage.getItem('token_expiry');
    
    if (!token || !tokenExpiry) {
      return false;
    }
    
    const expiryTime = parseInt(tokenExpiry, 10);
    const currentTime = Date.now();
    
    if (currentTime >= expiryTime) {
      logout();
      return false;
    }
    
    return true;
  };

  const value: AuthContextType = {
    isAuthenticated,
    accessToken,
    login,
    logout,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}